import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { OfferBanner } from './marketplace/OfferBanner';
import { MembersOnlyGate } from './marketplace/MembersOnlyGate';
import { Header, Footer } from './Layout';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Store, Loader2, ArrowLeft, ShoppingBag, MapPin, Building2,
  Tag, Crown, ArrowRight, CheckCircle, Flag, Wrench, Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DualPrice } from './DualPrice';
import { TIER_STYLES, tierKey } from '../utils/tierStyles';

const API = process.env.REACT_APP_BACKEND_URL;

// Inline image with fallback — used for service cards on detail page.
function ServiceImage({ src, alt, itemType }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    const Icon = itemType === 'product' ? Package : Wrench;
    return (
      <div className="w-full h-full bg-gradient-to-br from-[#041A3B] to-[#0A254D] flex items-center justify-center" data-testid="service-image-fallback">
        <Icon className="w-12 h-12 text-[#5C7599]/30" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      onError={() => setErrored(true)}
      data-testid="service-image-img"
    />
  );
}

const BUSINESS_TYPES = {
  food: 'Food & Beverage', retail: 'Retail & Shopping', health: 'Health & Wellness',
  beauty: 'Beauty & Salon', tech: 'Technology & IT', travel: 'Travel & Tourism',
  education: 'Education & Training', entertainment: 'Entertainment', automotive: 'Automotive',
  real_estate: 'Real Estate', professional: 'Professional Services', other: 'Other',
};

export function MerchantPartnerDetailPage() {
  const { merchantId } = useParams();
  const { user, api, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlightedItemId, setHighlightedItemId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    setLoading(true);
    const fetchDetail = async () => {
      try {
        // Authenticated request so the API can return the viewer's own tier price + discount.
        const { data } = await api.get(`/api/merchant-partners/${merchantId}`);
        setMerchant(data.merchant);
        setServices(data.services || []);
        setPlans(data.plans || []);
        // Canonicalize: if URL uses the UUID but a slug exists, swap silently.
        const slug = data.merchant?.slug;
        if (slug && slug !== merchantId) {
          window.history.replaceState(
            null,
            '',
            `/marketplace/${slug}${window.location.hash || ''}`,
          );
        }
      } catch (e) {
        console.error('Failed to load merchant:', e);
      }
      setLoading(false);
    };
    fetchDetail();
  }, [merchantId, user?.id, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // After services load, scroll to and highlight the item from `#item-<id>` hash (deep-link from "For You" / "Book Now").
  useEffect(() => {
    if (loading || services.length === 0) return;
    const hash = window.location.hash || '';
    const m = hash.match(/^#item-(.+)$/);
    if (!m) return;
    const itemId = m[1];
    setHighlightedItemId(itemId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="service-card-${itemId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    const t = setTimeout(() => setHighlightedItemId(null), 4000);
    return () => clearTimeout(t);
  }, [loading, services]);

  const handleBook = async (svc) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    const isProduct = svc?.item_type === 'product';
    try {
      await api.post(`/api/services/${svc.id}/avail`);
      toast.success(
        isProduct
          ? `Discount claim recorded for "${svc.name}". The merchant will honor your member discount at purchase.`
          : `Discount claim recorded for "${svc.name}". The merchant will contact you to schedule.`,
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record discount claim');
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(amount || 0);

  const isMember = user?.subscription_status === 'active';
  const userPlanId = user?.subscription_plan_id;

  // Get the user's plan price to determine tier level
  const getUserTierIndex = () => {
    if (!userPlanId || !plans.length) return -1;
    return plans.findIndex(p => p.id === userPlanId);
  };

  const getTierPrice = (service, planId) => {
    const tp = service.tier_prices || {};
    return tp[planId] || null;
  };

  if (!authLoading && !user) return <MembersOnlyGate />;

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  if (!merchant) {
    return (
      <MerchantDetailShell>
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <Store className="w-16 h-16 text-[#5C7599]/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Merchant Not Found</h3>
            <Link to="/marketplace">
              <Button variant="ghost" className="text-[#00E5FF] mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Merchant Partners
              </Button>
            </Link>
          </div>
        </main>
      </MerchantDetailShell>
    );
  }

  return (
    <MerchantDetailShell>
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-[#A0B4CC] hover:text-[#00E5FF] text-sm mb-6 transition-colors" data-testid="back-to-merchants-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Merchant Partners
          </Link>

          {/* Merchant Header */}
          {(() => {
            const detailTier = tierKey(merchant.subscription_plan);
            const detailTierStyle = detailTier ? TIER_STYLES[detailTier] : null;
            return (
          <div className="bg-[#041A3B] border border-white/10 rounded-2xl p-6 sm:p-8 mb-8" data-testid="merchant-detail-header">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div
                className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E676]/20 to-[#00E5FF]/10 flex items-center justify-center flex-shrink-0 overflow-hidden ${detailTierStyle ? `${detailTierStyle.ring} ${detailTierStyle.shadow}` : ''}`}
                data-testid="merchant-detail-tier-ring"
                data-tier={detailTier || 'none'}
              >
                {merchant.business_logo_url ? (
                  <img src={merchant.business_logo_url} alt={merchant.business_name}
                    className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <Store className="w-8 h-8 text-[#00E676]" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white">
                    {merchant.business_name}
                  </h1>
                  {false && detailTierStyle && (
                    <span
                      className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${detailTierStyle.labelBg}`}
                      data-testid="merchant-detail-tier-badge"
                    >
                      {detailTierStyle.label}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-[#A0B4CC] mb-3">
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-[#5C7599]" />
                    {BUSINESS_TYPES[merchant.business_type] || merchant.business_type}
                  </span>
                  {merchant.business_address && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-[#5C7599]" />
                      {merchant.business_address}
                    </span>
                  )}
                </div>
                {merchant.business_description && (
                  <p className="text-[#5C7599] text-sm leading-relaxed max-w-2xl">
                    {merchant.business_description}
                  </p>
                )}
              </div>
              {user && user.id !== merchant.id && (
                <button
                  onClick={() => navigate(`/support?merchant=${merchantId}`)}
                  className="text-[#FF1744] text-xs px-3 py-1.5 rounded-lg bg-[#FF1744]/5 hover:bg-[#FF1744]/10 border border-[#FF1744]/20 inline-flex items-center gap-1.5 transition-colors flex-shrink-0"
                  data-testid="report-merchant-btn">
                  <Flag className="w-3.5 h-3.5" /> Report this merchant
                </button>
              )}
            </div>
          </div>
            );
          })()}

          <OfferBanner m={merchant} isAuthenticated={!!user} />

          {/* Subscription Gate for non-members */}
          {!isMember && !merchant.has_offer && (
            <div className="mb-8 p-5 rounded-xl bg-gradient-to-r from-[#00E5FF]/10 to-[#007AFF]/10 border border-[#00E5FF]/30">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-[#00E5FF]" />
                  </div>
                  <div>
                    <h3 className="font-outfit text-base font-semibold text-white">
                      Subscribe to Access Member Discounts
                    </h3>
                    <p className="text-[#A0B4CC] text-sm">
                      Get exclusive tier-based discounts on all merchant services
                    </p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90 font-medium" data-testid="subscribe-cta-btn">
                    Subscribe Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Services Section */}
          <div className="mb-6">
            <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#00E5FF]" />
              Products & Services
              <span className="text-[#5C7599] text-sm font-normal ml-1">({services.length})</span>
            </h2>
          </div>

          {services.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-14 h-14 text-[#5C7599]/30 mx-auto mb-3" />
              <p className="text-[#5C7599]">This merchant hasn&apos;t listed any services yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map(svc => {
                const tierPrices = svc.tier_prices || {};
                const hasTierPricing = Object.keys(tierPrices).length > 0;
                const userTierIdx = getUserTierIndex();
                const userTierPrice = userPlanId ? getTierPrice(svc, userPlanId) : null;
                const displayPrice = isMember && userTierPrice ? userTierPrice : svc.regular_price;
                const discount = isMember && userTierPrice
                  ? Math.round(((svc.regular_price - userTierPrice) / svc.regular_price) * 100)
                  : 0;

                return (
                  <Card key={svc.id} className={`bg-[#041A3B] border-white/10 hover:border-[#00E5FF]/30 transition-all group overflow-hidden ${highlightedItemId === svc.id ? 'ring-2 ring-[#00E5FF] shadow-lg shadow-[#00E5FF]/20' : ''}`} data-testid={`service-card-${svc.id}`}>
                    {/* Service Image (always rendered; gracefully falls back if missing/broken) */}
                    <div className="h-40 overflow-hidden">
                      <ServiceImage src={svc.image_url} alt={svc.name} itemType={svc.item_type} />
                    </div>

                    <CardContent className="p-5">
                      {/* Category Badge */}
                      {svc.category && svc.category !== 'Other' && (
                        <Badge className="bg-[#00E5FF]/10 text-[#00E5FF] border-0 mb-3 text-[10px]">
                          {svc.category}
                        </Badge>
                      )}

                      <h3 className="font-outfit text-base font-semibold text-white mb-1.5">
                        {svc.name}
                      </h3>
                      <p className="text-[#5C7599] text-sm mb-4 line-clamp-2">
                        {svc.description}
                      </p>

                      {/* Pricing */}
                      <div className="mb-4">
                        <div className="flex items-end gap-2 flex-wrap">
                          <span className="font-outfit text-xl font-bold text-[#00E5FF]">
                            <DualPrice php={displayPrice} usdClassName="text-sm text-[#A0B4CC] font-normal ml-1" />
                          </span>
                          {discount > 0 && (
                            <span className="text-[#5C7599] text-sm line-through">
                              <DualPrice php={svc.regular_price} usdClassName="text-[#5C7599]/70" />
                            </span>
                          )}
                          {discount > 0 && (
                            <Badge className="bg-[#00E676]/15 text-[#00E676] border-0 text-[10px]">
                              {discount}% OFF
                            </Badge>
                          )}
                        </div>

                        {/* Tier Pricing Table */}
                        {hasTierPricing && plans.length > 0 && (
                          <div className="mt-3 space-y-1.5">
                            <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">
                              Member Tier Pricing
                            </p>
                            {plans.map((plan, idx) => {
                              const tp = tierPrices[plan.id];
                              if (!tp) return null;
                              const isUserTier = plan.id === userPlanId;
                              const tierDiscount = Math.round(((svc.regular_price - tp) / svc.regular_price) * 100);
                              return (
                                <div
                                  key={plan.id}
                                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs ${
                                    isUserTier
                                      ? 'bg-[#00E676]/10 border border-[#00E676]/30'
                                      : 'bg-white/3 border border-white/5'
                                  }`}
                                  data-testid={`tier-price-${plan.id}`}
                                >
                                  <span className={`font-medium flex items-center gap-1.5 ${isUserTier ? 'text-[#00E676]' : 'text-[#A0B4CC]'}`}>
                                    {isUserTier && <CheckCircle className="w-3 h-3" />}
                                    {plan.name}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className={`font-semibold ${isUserTier ? 'text-[#00E676]' : 'text-white'}`}>
                                      <DualPrice php={tp} usdClassName="text-[10px] opacity-70 ml-1" />
                                    </span>
                                    {tierDiscount > 0 && (
                                      <span className="text-[#00E676] text-[10px]">-{tierDiscount}%</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Book / Purchase Button — records a discount claim */}
                      {isMember ? (
                        <Button
                          onClick={() => handleBook(svc)}
                          className="w-full bg-[#00E5FF]/10 text-[#00E5FF] hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30"
                          data-testid={`book-service-${svc.id}`}
                        >
                          <ShoppingBag className="w-4 h-4 mr-2" /> {svc.item_type === 'product' ? 'Purchase' : 'Book'}
                        </Button>
                      ) : (
                        <Link to="/pricing" className="block">
                          <Button
                            variant="ghost"
                            className="w-full text-[#5C7599] hover:text-white border border-white/10"
                            data-testid={`subscribe-for-${svc.id}`}
                          >
                            Subscribe to {svc.item_type === 'product' ? 'Purchase' : 'Book'} <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </MerchantDetailShell>
  );
}

function MerchantDetailShell({ children }) {
  const { user } = useAuth();
  if (user) {
    return <MemberSidebarLayout>{children}</MemberSidebarLayout>;
  }
  return (
    <div className="min-h-screen bg-[#000B18]">
      <Header />
      {children}
      <Footer />
    </div>
  );
}
